export const FILTER_TYPES = {
  RANGE: "range",
  DATE_RANGE: "dateRange",
  SEARCH: "search",
  DROPDOWN: "dropdown",
};

export const createDefaultDeviceFilters = () => ({
  batteryLevel: {
    min: "0",
    max: "100",
  },
  agentCheckInDate: {
    start: "",
    end: "",
  },
  searchText: "",
  status: [],
  hasIp: [],
  chargingStatus: [],
  inKiosk: [],
  currentFolder: [],
});

const toFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toComparableTimestamp = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = new Date(value);
  const ts = parsed.getTime();
  return Number.isFinite(ts) ? ts : null;
};

const getDateStartMs = (dateString) => {
  if (!dateString) return null;
  const ts = new Date(`${dateString}T00:00:00`).getTime();
  return Number.isFinite(ts) ? ts : null;
};

const getDateEndMs = (dateString) => {
  if (!dateString) return null;
  const ts = new Date(`${dateString}T23:59:59.999`).getTime();
  return Number.isFinite(ts) ? ts : null;
};

const normalizeTextArray = (value) => {
  if (Array.isArray(value)) return value.map((part) => String(part || ""));
  if (value === null || value === undefined) return [];
  return [String(value)];
};

const matchesRange = (item, definition, filtersById) => {
  const rawValue = definition.getValue(item);
  const numericValue = toFiniteNumber(rawValue);
  if (numericValue === null) return false;

  const value = filtersById?.[definition.id] || {};
  const minInput = String(value.min ?? "").trim();
  const maxInput = String(value.max ?? "").trim();

  const defaultMin = toFiniteNumber(definition.defaultValue?.min);
  const defaultMax = toFiniteNumber(definition.defaultValue?.max);
  const min = minInput === "" ? defaultMin : toFiniteNumber(minInput);
  const max = maxInput === "" ? defaultMax : toFiniteNumber(maxInput);

  if (min !== null && numericValue < min) return false;
  if (max !== null && numericValue > max) return false;
  return true;
};

const matchesDateRange = (item, definition, filtersById) => {
  const value = filtersById?.[definition.id] || {};
  const startMs = getDateStartMs(String(value.start || "").trim());
  const endMs = getDateEndMs(String(value.end || "").trim());
  if (startMs === null && endMs === null) return true;

  const itemTimestamp = toComparableTimestamp(definition.getValue(item));
  if (itemTimestamp === null) return false;
  if (startMs !== null && itemTimestamp < startMs) return false;
  if (endMs !== null && itemTimestamp > endMs) return false;
  return true;
};

const matchesSearch = (item, definition, filtersById) => {
  const searchTerms = String(filtersById?.[definition.id] || "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (searchTerms.length === 0) return true;

  const searchableText = normalizeTextArray(definition.getValue(item))
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return searchTerms.some((term) => searchableText.includes(term));
};

const matchesDropdown = (item, definition, filtersById) => {
  const selectedValues = Array.isArray(filtersById?.[definition.id]) ? filtersById[definition.id] : [];
  if (selectedValues.length === 0) return true;

  const normalizeOption = definition.normalizeOption || ((value) => String(value || "").toLowerCase());
  const normalizedSelection = selectedValues.map(normalizeOption);
  const currentValue = normalizeOption(definition.getValue(item));
  return normalizedSelection.includes(currentValue);
};

const isRangeFilterActive = (definition, value) => {
  const defaultMin = String(definition.defaultValue?.min ?? "").trim();
  const defaultMax = String(definition.defaultValue?.max ?? "").trim();
  const min = String(value?.min ?? "").trim();
  const max = String(value?.max ?? "").trim();
  return min !== defaultMin || max !== defaultMax;
};

export const hasActiveFilters = (definitions, filtersById) =>
  definitions.some((definition) => {
    const value = filtersById?.[definition.id];

    if (definition.type === FILTER_TYPES.RANGE) {
      return isRangeFilterActive(definition, value);
    }
    if (definition.type === FILTER_TYPES.DATE_RANGE) {
      return Boolean(String(value?.start || "").trim() || String(value?.end || "").trim());
    }
    if (definition.type === FILTER_TYPES.SEARCH) {
      return Boolean(String(value || "").trim());
    }
    if (definition.type === FILTER_TYPES.DROPDOWN) {
      return Array.isArray(value) && value.length > 0;
    }
    return false;
  });

export const applyDefinedFilters = (items, definitions, filtersById) =>
  items.filter((item) =>
    definitions.every((definition) => {
      if (definition.type === FILTER_TYPES.RANGE) {
        return matchesRange(item, definition, filtersById);
      }
      if (definition.type === FILTER_TYPES.DATE_RANGE) {
        return matchesDateRange(item, definition, filtersById);
      }
      if (definition.type === FILTER_TYPES.SEARCH) {
        return matchesSearch(item, definition, filtersById);
      }
      if (definition.type === FILTER_TYPES.DROPDOWN) {
        return matchesDropdown(item, definition, filtersById);
      }
      return true;
    })
  );
